import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { Empty, Loader, PageHeader, btn } from "@/components/Page";
import { LeagueBadge } from "@/components/triumvirate/LeagueBadge";
import { TRIUMVIRATE_MODE } from "@/components/triumvirate/TriumvirateSectionChrome";

function rewardText(reward) {
  if (!reward) return "No reward recorded";
  const parts = [];
  if (reward.title) parts.push(reward.title);
  if (reward.gold) parts.push(`${Number(reward.gold).toLocaleString()} gold`);
  if (reward.follis) parts.push(`${Number(reward.follis).toLocaleString()} follis`);
  if (reward.chest_item_id && reward.chest_qty) parts.push(`${reward.chest_item_id} × ${reward.chest_qty}`);
  return parts.join(" · ");
}

export default function LudusTriasArchive() {
  const [listData, setListData] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    async function loadArchive() {
      setLoading(true);
      try {
        const { data } = await api.get("/triumvirate/archive");
        setListData(data);
        const firstSeason = data.seasons?.[0]?.season || "";
        setSelectedSeason(firstSeason);
        if (firstSeason) {
          setDetailLoading(true);
          const detail = await api.get(`/triumvirate/archive/${firstSeason}`);
          setDetailData(detail.data);
          setDetailLoading(false);
        }
      } catch (error) {
        toast.error(apiErr(error, "Could not load the Ludus Trias archive"));
      } finally {
        setLoading(false);
      }
    }
    loadArchive();
  }, []);

  const selectSeason = async (seasonId) => {
    setSelectedSeason(seasonId);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/triumvirate/archive/${seasonId}`);
      setDetailData(data);
    } catch (error) {
      toast.error(apiErr(error, "Could not load that archive season"));
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && !listData) return <Loader label="Loading archive…" />;

  return (
    <div data-testid="ludus-trias-archive-page" className="space-y-6">
      <PageHeader
        title="Ludus Trias Archive"
        subtitle="Review completed monthly seasons, past winners, and final ladder snapshots from the unified live ladder."
        action={<Link data-testid="ludus-trias-archive-back-link" to="/ludus-trias" className={btn("ghost")}>Back to Ludus Trias</Link>}
      />

      <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <div data-testid="ludus-trias-archive-list-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5">
          <div className="font-cinzel text-2xl text-amber-200">Completed Seasons</div>
          <div className="mt-4 space-y-3" data-testid="ludus-trias-archive-list">
            {(listData?.seasons || []).length === 0 ? (
              <Empty label="No completed Ludus Trias seasons have been archived yet." />
            ) : (
              (listData?.seasons || []).map((season) => (
                <button
                  key={season.season}
                  type="button"
                  data-testid={`ludus-trias-archive-season-${season.season}`}
                  onClick={() => selectSeason(season.season)}
                  className={`w-full text-left border rounded-sm p-4 ${selectedSeason === season.season ? "border-amber-500 bg-amber-950/20" : "border-zinc-800 bg-zinc-950/45"}`}
                >
                  <div className="font-cinzel text-lg text-amber-200">{season.season_label}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.16em] text-zinc-500">{season.season}</div>
                </button>
              ))
            )}
          </div>
        </div>

        <div data-testid="ludus-trias-archive-detail-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5">
          {detailLoading ? (
            <Loader label="Opening season…" />
          ) : !detailData ? (
            <Empty label="Select a completed season to inspect the final ladders." />
          ) : (
            <div className="space-y-5">
              <div>
                <div data-testid="ludus-trias-archive-season-label" className="font-cinzel text-2xl text-amber-200">{detailData.season_label}</div>
                <div className="mt-1 text-sm text-zinc-500">Finalized {detailData.finalized_at ? new Date(detailData.finalized_at).toLocaleString() : "-"}</div>
              </div>

              <div data-testid="ludus-trias-archive-league-overall" className="border border-zinc-800 bg-zinc-950/45 rounded-sm p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId="ludus-trias-archive-badge-overall" />
                  <div className="text-sm text-zinc-500">Final ladder snapshot</div>
                </div>
                <div className="mt-4 space-y-2" data-testid="ludus-trias-archive-ladder-overall">
                  {(detailData.final_ladders?.overall || []).length === 0 ? (
                    <Empty label="No final placements recorded for this season." />
                  ) : (
                    (detailData.final_ladders?.overall || []).slice(0, 10).map((entry) => (
                      <div key={`overall-${entry.rank}-${entry.user_id}`} data-testid={`ludus-trias-archive-row-overall-${entry.rank}`} className="border border-zinc-900 bg-zinc-950/35 rounded-sm p-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="font-cinzel text-base text-amber-200">#{entry.rank} {entry.user_name}</div>
                            <div className="mt-1 text-xs uppercase tracking-[0.16em] text-zinc-500">Rating {Number(entry.rating || 0).toLocaleString()} · {entry.wins || 0}W · {entry.losses || 0}L · Avg {Number(entry.score || 0).toLocaleString()}</div>
                          </div>
                        </div>
                        {entry.reward ? <div className="mt-2 text-sm text-zinc-300">Reward: {rewardText(entry.reward)}</div> : null}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
